/**
 * Marketing namespace aggregator.
 *
 * The public marketing pages (use-cases, integrations, pricing) keep their
 * copy in per-page fragments here rather than inline in the giant `en.ts` /
 * `nl.ts` so several pages can be translated independently without colliding
 * in one file. `en.ts` / `nl.ts` spread `marketingEn` / `marketingNl` into the
 * top-level dictionary; every key lands under its own namespace (e.g.
 * `ucTicketQuestions`, `intgSlack`, `pricingPage`, `mk`).
 *
 * NL keys fall back to EN at runtime when missing (see translate()), so a
 * partially-translated fragment still renders meaningful copy.
 */

import { mkEn, mkNl } from "./_shared";
import { aboutEn, aboutNl } from "./about";
import { businessValuePageEn, businessValuePageNl } from "./business-value";
import { marketingHubsEn, marketingHubsNl } from "./hubs";
import { integrationsMatrixEn, integrationsMatrixNl } from "./integrations-matrix";
import { ucTicketQuestionsEn, ucTicketQuestionsNl } from "./uc-ticket-questions";
import { ucApprovedRepliesEn, ucApprovedRepliesNl } from "./uc-approved-replies";
import { ucRemediationEn, ucRemediationNl } from "./uc-remediation";
import { ucHrGovernanceEn, ucHrGovernanceNl } from "./uc-hr-governance";
import { ucSupportEn, ucSupportNl } from "./uc-support";
import { ucEngineeringEn, ucEngineeringNl } from "./uc-engineering";
import { videoKitEn, videoKitNl } from "./videos";
import { ucApprovalLedgerEn, ucApprovalLedgerNl } from "./uc-approval-ledger";
import { ucDeploymentEn, ucDeploymentNl } from "./uc-deployment";
import { intgSlackEn, intgSlackNl } from "./intg-slack";
import { intgTeamsEn, intgTeamsNl } from "./intg-teams";
import { intgJiraEn, intgJiraNl } from "./intg-jira";
import { intgFreshdeskEn, intgFreshdeskNl } from "./intg-freshdesk";
import { intgOdooEn, intgOdooNl } from "./intg-odoo";
import { intgTopdeskEn, intgTopdeskNl } from "./intg-topdesk";
import { intgAfasEn, intgAfasNl } from "./intg-afas";
import { intgYouforceEn, intgYouforceNl } from "./intg-youforce";
import { intgGithubEn, intgGithubNl } from "./intg-github";
import { intgGitlabEn, intgGitlabNl } from "./intg-gitlab";
import { intgArgoEn, intgArgoNl } from "./intg-argo";
import { intgFcAgentEn, intgFcAgentNl } from "./intg-fc-agent";
import { changeValidationEn, changeValidationNl } from "./change-validation";
import { pricingPageEn, pricingPageNl } from "./pricing";
import { comparePagesEn, comparePagesNl } from "./compare";
import { productPageEn, productPageNl } from "./product";
import { securityPageEn, securityPageNl } from "./security";

export const marketingEn = {
  mk: mkEn,
  about: aboutEn,
  businessValuePage: businessValuePageEn,
  marketingHubs: marketingHubsEn,
  integrationsMatrix: integrationsMatrixEn,
  ucTicketQuestions: ucTicketQuestionsEn,
  ucApprovedReplies: ucApprovedRepliesEn,
  ucRemediation: ucRemediationEn,
  ucHrGovernance: ucHrGovernanceEn,
  ucSupport: ucSupportEn,
  ucEngineering: ucEngineeringEn,
  videoKit: videoKitEn,
  ucApprovalLedger: ucApprovalLedgerEn,
  ucDeployment: ucDeploymentEn,
  intgSlack: intgSlackEn,
  intgTeams: intgTeamsEn,
  intgJira: intgJiraEn,
  intgFreshdesk: intgFreshdeskEn,
  intgOdoo: intgOdooEn,
  intgTopdesk: intgTopdeskEn,
  intgAfas: intgAfasEn,
  intgYouforce: intgYouforceEn,
  intgGithub: intgGithubEn,
  intgGitlab: intgGitlabEn,
  intgArgo: intgArgoEn,
  intgFcAgent: intgFcAgentEn,
  changeValidation: changeValidationEn,
  pricingPage: pricingPageEn,
  comparePages: comparePagesEn,
  productPage: productPageEn,
  securityPage: securityPageEn,
};

export const marketingNl = {
  mk: mkNl,
  about: aboutNl,
  businessValuePage: businessValuePageNl,
  marketingHubs: marketingHubsNl,
  integrationsMatrix: integrationsMatrixNl,
  ucTicketQuestions: ucTicketQuestionsNl,
  ucApprovedReplies: ucApprovedRepliesNl,
  ucRemediation: ucRemediationNl,
  ucHrGovernance: ucHrGovernanceNl,
  ucSupport: ucSupportNl,
  ucEngineering: ucEngineeringNl,
  videoKit: videoKitNl,
  ucApprovalLedger: ucApprovalLedgerNl,
  ucDeployment: ucDeploymentNl,
  intgSlack: intgSlackNl,
  intgTeams: intgTeamsNl,
  intgJira: intgJiraNl,
  intgFreshdesk: intgFreshdeskNl,
  intgOdoo: intgOdooNl,
  intgTopdesk: intgTopdeskNl,
  intgAfas: intgAfasNl,
  intgYouforce: intgYouforceNl,
  intgGithub: intgGithubNl,
  intgGitlab: intgGitlabNl,
  intgArgo: intgArgoNl,
  intgFcAgent: intgFcAgentNl,
  changeValidation: changeValidationNl,
  pricingPage: pricingPageNl,
  comparePages: comparePagesNl,
  productPage: productPageNl,
  securityPage: securityPageNl,
};
